// File: packages/agent-kit/src/conversations/useConversation/conversationActions.ts

import type {
  UIMessage,
  SSEMessageEventData,
  SSEResponseChunkEvent,
  SSECompleteEvent,
  SSEProgressUpdateEvent,
  InitTestChatResponse,
  Message,
  ToolResultPayload,
  UUID,
} from '@dopomogai/types';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import { AgentKitError } from '../../types';
import type { SendMessageOptions } from '../../types';
import {
  processBackendMessagesToUI,
  handleProgressUpdate,
  handleResponseChunk,
  handleComplete,
  handleStreamEnd,
  handleError,
} from './sseHandlers';

const NEW_CHAT_ID_PLACEHOLDER = 'new-chat-placeholder';

type ChatService = {
  streamTestConversation: (
    apiBaseUrl: string,
    agentId: UUID,
    body: Record<string, any>,
    wat: string,
    onMessage: (data: SSEMessageEventData) => void,
    onError: (error: any) => void,
    onClose: () => void,
    onOpen?: (openEvent: { headers?: Record<string, string> }) => void
  ) => { close: () => void };
  submitToolResultAndStream: (
    apiBaseUrl: string,
    agentId: UUID,
    conversationId: UUID,
    toolCallId: string,
    payload: ToolResultPayload,
    wat: string,
    onMessage: (data: SSEMessageEventData) => void,
    onError: (error: any) => void,
    onClose: () => void,
    onOpen?: (openEvent: { headers?: Record<string, string> }) => void
  ) => { close: () => void };
};

type ConversationService = {
  getConversationById: (conversationId: string, includeInternalParts?: boolean) => Promise<any>;
};

export interface BuildConversationActionsArgs {
  agentId: UUID | null;
  activeConversationId: UUID | null;
  internalAgentId: UUID | null;

  chatService: ChatService;
  conversationService: ConversationService;

  ensureSession: (injectionContext?: string) => Promise<{ session: InitTestChatResponse; agentIdForStream: UUID }>;
  applyOpenHeaders: (headers: Record<string, string>, setConversationId: (id: UUID) => void) => void;
  sessionRef: React.MutableRefObject<InitTestChatResponse | null>;

  options?: {
    onSseEvent?: (event: SSEMessageEventData) => void;
    authMode?: 'test' | 'widget';
  };

  messages: UIMessage[];
  setMessages: Dispatch<SetStateAction<UIMessage[]>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isSynthesizing: boolean;
  setIsSynthesizing: Dispatch<SetStateAction<boolean>>;
  setError: (e: AgentKitError | null) => void;
  setUsage: Dispatch<SetStateAction<{ cost: number; tokensInput: number; tokensOutput: number }>>;
  streamConversationId: UUID | null;
  setStreamConversationId: Dispatch<SetStateAction<UUID | null>>;

  assistantMessageIdRef: MutableRefObject<string | null>;
  streamingContentRef: MutableRefObject<string>;
  streamControllerRef: MutableRefObject<{ close: () => void } | null>;
}

export function buildConversationActions(args: BuildConversationActionsArgs) {
  const {
    agentId,
    activeConversationId,
    internalAgentId,
    chatService,
    conversationService,
    ensureSession,
    applyOpenHeaders,
    sessionRef,
    options,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    isSynthesizing,
    setIsSynthesizing,
    setError,
    setUsage,
    streamConversationId,
    setStreamConversationId,
    assistantMessageIdRef,
    streamingContentRef,
    streamControllerRef,
  } = args;

  const loadHistory = async (conversationIdToLoad: string) => {
    if (conversationIdToLoad === NEW_CHAT_ID_PLACEHOLDER) return;

    try {
      const { agentIdForStream } = await ensureSession();
      const idForHistory = options?.authMode === 'widget' ? agentIdForStream : agentId;

      if (!idForHistory) {
        setError(new AgentKitError('Agent ID is required to load history.'));
        return;
      }

      setIsLoading(true);

      const includeInternalParts = options?.authMode !== 'widget';
      const historyResponse = await conversationService.getConversationById(conversationIdToLoad, includeInternalParts);
      if (historyResponse && historyResponse.error) {
        throw new AgentKitError(historyResponse.message || 'Failed to load conversation history.', historyResponse);
      }

      const uiMessages = processBackendMessagesToUI(historyResponse.messages as Message[]);
      setMessages(uiMessages);
      setStreamConversationId(conversationIdToLoad as UUID);

      const calculatedUsage = (historyResponse.messages as Message[]).reduce(
        (acc, msg) => {
          acc.cost += msg.cost ?? 0;
          acc.tokensInput += msg.tokens_input ?? 0;
          acc.tokensOutput += msg.tokens_output ?? 0;
          return acc;
        },
        { cost: 0, tokensInput: 0, tokensOutput: 0 }
      );
      setUsage(calculatedUsage);
    } catch (e: any) {
      setError(e instanceof AgentKitError ? e : new AgentKitError('Failed to load conversation history.', e));
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (text: string, sendMessageOptions?: SendMessageOptions): Promise<void> => {
    if (!internalAgentId && options?.authMode !== 'widget') {
      setError(new AgentKitError('Cannot send message: Agent ID is required for test mode.'));
      return;
    }
    if (isLoading || isSynthesizing) return;

    const optimisticId = `user-${Date.now()}`;
    const userMessage: UIMessage = {
      id: optimisticId,
      type: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retryPayload: { text, options: sendMessageOptions },
    };
    setMessages(prev => [...prev, userMessage]);
    setError(null);
    setIsLoading(true);

    try {
      const { session, agentIdForStream } = await ensureSession(sendMessageOptions?.injectionContext);
      const forceNew = (sendMessageOptions as any)?.forceNew === true;
      
      const explicitConvId = (sendMessageOptions as any)?.conversationId as UUID | null | undefined;
      let baseConvId = explicitConvId ?? streamConversationId ?? activeConversationId ?? undefined;

      // CRITICAL FIX: Guard against placeholder ID
      if (baseConvId === NEW_CHAT_ID_PLACEHOLDER) {
        baseConvId = undefined;
      }

      const requestBody: Record<string, any> = { user_message: text };
      
      // Only attach ID if valid and not forcing new
      if (!forceNew && baseConvId) {
        requestBody.conversation_id = baseConvId;
      }
      
      if (forceNew || !baseConvId) {
        setStreamConversationId(null);
        requestBody.start_new = true;
        requestBody.conversation_id = undefined;
      }

      const runOverrides = (sendMessageOptions as any)?.runOverrides;
      if (runOverrides && typeof runOverrides === 'object') {
        requestBody.run_overrides = runOverrides;
      }

      const attachments = (sendMessageOptions as any)?.attachments as { fileId: string }[] | undefined;
      if (Array.isArray(attachments) && attachments.length > 0) {
        requestBody.parts = [
          { type: 'text', content: text },
          ...attachments.map(a => ({ type: 'file', file_id: a.fileId })),
        ];
        requestBody.user_message = undefined;
      }

      const includeInternalParts = options?.authMode !== 'widget';
      const payload = {
        ...requestBody,
        initial_context: sendMessageOptions?.injectionContext,
        turn_prompt_override: sendMessageOptions?.turnPromptOverride,
        visitor_id: (sessionRef.current as any)?.visitor_id,
      };

      streamControllerRef.current = chatService.streamTestConversation(
        session.api_base_url,
        agentIdForStream,
        payload,
        session.wat,
        (parsedData: SSEMessageEventData) => {
          if (typeof parsedData !== 'object' || parsedData === null) return;
          options?.onSseEvent?.(parsedData);

          if ('conversation_id' in parsedData && parsedData.conversation_id) {
            setStreamConversationId(parsedData.conversation_id as UUID);
          }

          switch (parsedData.type) {
            case 'progress_update':
              handleProgressUpdate(parsedData as SSEProgressUpdateEvent, setMessages);
              break;
            case 'response_chunk':
              handleResponseChunk(parsedData as SSEResponseChunkEvent, assistantMessageIdRef, streamingContentRef, setMessages);
              break;
            case 'complete':
              handleComplete(parsedData as SSECompleteEvent, assistantMessageIdRef, streamingContentRef, setMessages, setUsage, optimisticId);
              if (includeInternalParts && (parsedData as SSECompleteEvent).conversation_id) {
                const cid = (parsedData as SSECompleteEvent).conversation_id!;
                // Small delay to ensure DB consistency
                setTimeout(() => loadHistory(cid), 100);
              }
              break;
            case 'stream_end':
              handleStreamEnd(assistantMessageIdRef, streamingContentRef, setMessages);
              break;
            case 'error':
              handleError(parsedData, setError, setMessages, optimisticId, assistantMessageIdRef, streamingContentRef);
              break;
            default:
              break;
          }
        },
        (errorDetails: any) => {
          setIsLoading(false);
          handleError(errorDetails, setError, setMessages, optimisticId, assistantMessageIdRef, streamingContentRef);
        },
        () => {
          setIsLoading(false);
        },
        (openEvent: { headers?: Record<string, string> }) => {
          const hdrs = openEvent?.headers || {};
          applyOpenHeaders(hdrs, (id: UUID) => setStreamConversationId(id));
        }
      );
    } catch (e: any) {
      setIsLoading(false);
      setError(e instanceof AgentKitError ? e : new AgentKitError('Failed to send message.', e));
      setMessages(prev => prev.map(m => (m.id === optimisticId ? { ...m, status: 'error' } : m)));
      assistantMessageIdRef.current = null;
      streamingContentRef.current = '';
    }
  };

  const submitToolResult = async (toolCallId: string, result: any): Promise<void> => {
    const convId = streamConversationId || activeConversationId;
    const agentIdForStream = options?.authMode === 'widget' ? internalAgentId : agentId;

    if (!agentIdForStream || !convId || convId === NEW_CHAT_ID_PLACEHOLDER) {
      setError(new AgentKitError('Cannot submit tool result: Agent or Conversation ID is invalid.'));
      return;
    }
    if (isLoading || isSynthesizing) return;

    const lastToolCall = messages.find(m => m.id === toolCallId);
    const toolName = lastToolCall?.type === 'tool_call_start' ? lastToolCall.tool_name : 'unknown';

    const toolResult: UIMessage = {
      id: `resp-for-${toolCallId}`,
      type: 'tool_response_end',
      tool_call_id: toolCallId,
      tool_name: toolName,
      output: result,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, toolResult]);
    setError(null);
    setIsSynthesizing(true);

    try {
      const session = sessionRef.current;
      if (!session) {
        const { session: newSession } = await ensureSession();
        if (!newSession) throw new AgentKitError('Session not initialized. Cannot submit tool result.');
        sessionRef.current = newSession;
      }

      const nonNullSession = sessionRef.current!;
      const resultPayload: ToolResultPayload = { tool_name: toolName, output: result };

      streamControllerRef.current = (chatService as any).submitToolResultAndStream(
        nonNullSession.api_base_url,
        agentIdForStream,
        convId,
        toolCallId,
        resultPayload,
        nonNullSession.wat,
        (parsedData: SSEMessageEventData) => {
          if (typeof parsedData !== 'object' || parsedData === null) return;
          options?.onSseEvent?.(parsedData);

          if (parsedData.type === 'progress_update') {
            handleProgressUpdate(parsedData as SSEProgressUpdateEvent, setMessages);
            return;
          }

          switch (parsedData.type) {
            case 'response_chunk':
              handleResponseChunk(parsedData as SSEResponseChunkEvent, assistantMessageIdRef, streamingContentRef, setMessages);
              break;
            case 'complete':
              handleComplete(parsedData as SSECompleteEvent, assistantMessageIdRef, streamingContentRef, setMessages, setUsage);
              break;
            case 'stream_end':
              handleStreamEnd(assistantMessageIdRef, streamingContentRef, setMessages);
              break;
            case 'error':
              handleError(parsedData, setError, setMessages, undefined, assistantMessageIdRef, streamingContentRef);
              break;
            default:
              break;
          }
        },
        (errorDetails: any) => {
          setIsSynthesizing(false);
          setError(new AgentKitError(errorDetails.message || 'A stream connection error occurred during synthesis.', errorDetails));
          assistantMessageIdRef.current = null;
          streamingContentRef.current = '';
        },
        () => {
          setIsSynthesizing(false);
        },
        (openEvent: { headers?: Record<string, string> }) => {
          const hdrs = openEvent?.headers || {};
          applyOpenHeaders(hdrs, (id: UUID) => setStreamConversationId(id));
        }
      );
    } catch (e: any) {
      setIsSynthesizing(false);
      setError(e instanceof AgentKitError ? e : new AgentKitError('Failed to submit tool result.', e));
    }
  };

  const retryMessage = (messageToRetry: UIMessage) => {
    if (!('retryPayload' in messageToRetry) || !messageToRetry.retryPayload) return;
    setMessages(prev => prev.filter(m => m.id !== messageToRetry.id));
    const payload = messageToRetry.retryPayload as { text: string; options?: SendMessageOptions };
    void sendMessage(payload.text, payload.options);
  };

  return { loadHistory, sendMessage, submitToolResult, retryMessage };
}